import type { Category, Product, ProductIngredient, ProductUnit } from './mock-data'

/**
 * 182 (1.59) — „ფასში შედის": a drink takes its cup.
 *
 * The owner says once, on a product or on a whole category, what ONE unit sold
 * takes off the shelf — 1 cup, 1 lid, 7 g of a kg bag. The price stays the
 * drink's. THE SERVER DECIDES what a sale takes (`private.recipe_of`, used by
 * create_sale); this file is the same rule in TS, for what the till only
 * shows or guesses: the hint under a cart line, the add-on strip that must not
 * offer an included cup, and the shelf an offline sale takes before the server
 * has seen it. Every case here has a twin in migrations/proof/182_ingredients.sql.
 */

/** The slice of the store the recipe rule needs. `StoreState` satisfies it. */
export type IngredientWorld = {
  products: Product[]
  categories: Category[]
  productIngredients: ProductIngredient[]
}

/** Above this per unit the database refuses the row (182's CHECK). */
export const INGREDIENT_QTY_MAX = 1000

// Recipe quantities are at most three decimals (whole grams / millilitres);
// six is enough to keep 3 × 0.007 from printing 0.021000000000000001.
const tidy = (n: number) => Math.round(n * 1e6) / 1e6

const byOrder = (a: ProductIngredient, b: ProductIngredient) =>
  a.sort_order - b.sort_order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

/**
 * What one unit of `productId` takes, nearest scope first.
 *
 * The product's own rows, then its category's, then each ancestor's (up to
 * ten — a category cycle cannot hang the till); the nearest scope wins for an
 * ingredient named twice, so one coffee can take two cups while the category
 * says one.
 *
 * A row of 0 at a nearer scope means „not here": an espresso in a ceramic cup
 * drops the category's lid.
 *
 * EMPTY for a product not sold by the piece — a recipe is per unit, and 0.15 kg
 * of ground coffee is not 0.15 of a cup. EMPTY for a product that is itself an
 * ingredient of anything in the shop: one level deep. An owner files the cup in
 * „ცხელი სასმელი" next to the coffee — the obvious place — and without this a
 * cup sold on its own (SAMGORI sold 105 that way in a month) would take a second
 * cup and a lid. An ADD-ON keeps its own recipe (Codex's design review #5: a
 * cake offered with a coffee still takes its box).
 */
export function resolveIngredients(w: IngredientWorld, productId: string): ProductIngredient[] {
  const product = w.products.find((p) => p.id === productId)
  if (!product || product.unit !== 'piece') return []
  if (w.productIngredients.some((r) => r.ingredient_id === productId && r.qty > 0)) return []
  return winners(w, product, true).filter((r) => r.qty > 0)
}

/**
 * What the product's category (and its ancestors) give it, without the
 * product's own rows — the product tab of the dialog shows these, each with its
 * „ამ პროდუქტზე არა" toggle.
 */
export function inheritedIngredients(w: IngredientWorld, productId: string): ProductIngredient[] {
  const product = w.products.find((p) => p.id === productId)
  if (!product) return []
  return winners(w, product, false).filter((r) => r.qty > 0)
}

/** Nearest scope first, one row per ingredient — exclusions (qty 0) still in. */
function winners(w: IngredientWorld, product: Product, own: boolean): ProductIngredient[] {
  const chain: string[] = []
  let cid: string | null = product.category_id || null
  for (let i = 0; cid && i < 10 && !chain.includes(cid); i++) {
    chain.push(cid)
    cid = w.categories.find((c) => c.id === cid)?.parent_id ?? null
  }

  const scopes = [
    ...(own ? [w.productIngredients.filter((r) => r.product_id === product.id).sort(byOrder)] : []),
    ...chain.map((c) => w.productIngredients.filter((r) => r.category_id === c).sort(byOrder)),
  ]
  const seen = new Set<string>()
  const out: ProductIngredient[] = []
  for (const rows of scopes) {
    for (const r of rows) {
      if (r.ingredient_id === product.id || seen.has(r.ingredient_id)) continue
      seen.add(r.ingredient_id)
      out.push(r)
    }
  }
  return out
}

/**
 * What a cart takes off the shelf through its recipes, one figure per
 * ingredient in first-seen order. Only an ESTIMATE — for an offline sale's
 * cached shelf; online, the till moves its copy by the lines the server wrote.
 */
export function ingredientMoves(
  w: IngredientWorld,
  lines: { product_id?: string | null; quantity: number }[],
): { product_id: string; quantity: number }[] {
  const total = new Map<string, number>()
  for (const line of lines) {
    if (!line.product_id) continue
    for (const r of resolveIngredients(w, line.product_id)) {
      total.set(r.ingredient_id, tidy((total.get(r.ingredient_id) ?? 0) + r.qty * line.quantity))
    }
  }
  return [...total].map(([product_id, quantity]) => ({ product_id, quantity }))
}

/** A ₾0 line the server wrote for an ingredient — never something the customer bought. */
export function isIngredientLine(i: { ingredient_of?: string | null }): boolean {
  return i.ingredient_of != null
}

export type IngredientDisplayUnit = 'piece' | 'g' | 'ml' | 'm'

/** A stored recipe amount as the owner reads it: 0.007 kg → 7 g, 0.03 l → 30 ml. */
export function toDisplayQty(qty: number, unit: ProductUnit): { value: number; unit: IngredientDisplayUnit } {
  if (unit === 'kg') return { value: Math.round(qty * 1e6) / 1e3, unit: 'g' }
  if (unit === 'l') return { value: Math.round(qty * 1e6) / 1e3, unit: 'ml' }
  if (unit === 'm') return { value: tidy(qty), unit: 'm' }
  return { value: tidy(qty), unit: 'piece' }
}

/**
 * A part as words: „2 ჭიქა", „7 გ ყავა", „30 მლ რძე". `units` comes from the
 * dictionary (this file stays pure). Pieces carry no unit word; with
 * `bareOne` a single piece is just its name — the till hint reads „ფასში:
 * ჭიქა, თავსახური, 7 გ ყავა".
 */
export function ingredientPhrase(
  qty: number,
  unit: ProductUnit,
  name: string,
  units: { piece: string; g: string; ml: string; m: string },
  opts: { bareOne?: boolean } = {},
): string {
  const d = toDisplayQty(qty, unit)
  if (d.unit === 'piece') return opts.bareOne && d.value === 1 ? name : `${d.value} ${name}`
  return `${d.value} ${units[d.unit]} ${name}`
}

/**
 * The till's muted hint under a cart line — „ჭიქა, თავსახური, 7 გ ყავა" — or
 * null when the product includes nothing. What the SERVER will take; the till
 * only shows it (never on the receipt, the display or the kiosk).
 */
export function recipeHint(
  w: IngredientWorld,
  productId: string,
  units: { piece: string; g: string; ml: string; m: string },
): string | null {
  const parts = resolveIngredients(w, productId)
  if (parts.length === 0) return null
  const named = parts.flatMap((r) => {
    const p = w.products.find((x) => x.id === r.ingredient_id)
    return p ? [ingredientPhrase(r.qty, p.unit, p.name, units, { bareOne: true })] : []
  })
  return named.length ? named.join(', ') : null
}

/**
 * What the owner typed, as the database stores it — or null when the database
 * would refuse it: whole pieces, whole grams / millilitres, metres to the
 * millimetre, above 0 and at most INGREDIENT_QTY_MAX per unit.
 */
export function fromDisplayQty(value: number, unit: ProductUnit): number | null {
  if (!Number.isFinite(value) || value <= 0) return null
  const whole = (n: number) => Math.abs(n - Math.round(n)) < 1e-9
  let qty: number
  if (unit === 'kg' || unit === 'l') {
    if (!whole(value)) return null
    qty = Math.round(value) / 1000
  } else if (unit === 'm') {
    if (!whole(value * 1000)) return null
    qty = Math.round(value * 1000) / 1000
  } else {
    if (!whole(value)) return null
    qty = Math.round(value)
  }
  return qty > INGREDIENT_QTY_MAX ? null : qty
}

/**
 * A sale's lines with each ingredient under the drink it rode with, the
 * drinks in their own order. A part whose drink is not in the list (a partial
 * read) stands as a line of its own rather than vanishing.
 */
export function nestIngredientLines<T extends { id: string; ingredient_of?: string | null }>(
  items: T[],
): { line: T; parts: T[] }[] {
  const out: { line: T; parts: T[] }[] = []
  const at = new Map<string, number>()
  for (const it of items) {
    if (isIngredientLine(it)) continue
    at.set(it.id, out.length)
    out.push({ line: it, parts: [] })
  }
  for (const it of items) {
    if (!isIngredientLine(it)) continue
    const i = at.get(it.ingredient_of as string)
    if (i === undefined) out.push({ line: it, parts: [] })
    else out[i].parts.push(it)
  }
  return out
}

/**
 * What the customer bought: a sale's lines without the ₾0 parts the server
 * wrote. The fiscal retry, the outgoing waybill (RS.GE), the exchange paper and
 * the return dialog read these — a cup is never on a paper the customer or the
 * tax office sees.
 */
export function soldLines<T extends { ingredient_of?: string | null }>(items: T[]): T[] {
  return items.filter((i) => !isIngredientLine(i))
}

/**
 * Units already returned against a sale, per product, counting only what the
 * customer handed back — not the cups the server put back with a coffee, or a
 * paid cup would read as returned once its coffee came back.
 */
export function returnedSoFar(
  items: { sale_id: string; product_id?: string | null; quantity: number; ingredient_of?: string | null }[],
  returnIds: Set<string>,
): Map<string, number> {
  const back = new Map<string, number>()
  for (const it of items) {
    if (!returnIds.has(it.sale_id) || !it.product_id || isIngredientLine(it)) continue
    back.set(it.product_id, (back.get(it.product_id) ?? 0) + it.quantity)
  }
  return back
}

/**
 * The report's per-product rows. A part's cost goes to the drink it rode with —
 * the coffee's row shows the cost of its cup and lid too (the „exact cost per
 * cup" the owner liked) — with no quantity and no revenue of its own: a cup is
 * not a best-seller. The costs still add up to the period's COGS (Codex #7).
 * `sign` is +1 for a sale's line, −1 for a return's.
 */
export function foldPerProduct<
  T extends { id: string; sale_id: string; product_id: string | null; product_name: string; quantity: number; ingredient_of?: string | null },
>(
  items: T[],
  sign: (saleId: string) => 1 | -1,
  revenueOf: (it: T) => number,
  unitCostOf: (it: T) => number,
): Map<string, { product_id: string | null; name: string; qty: number; revenue: number; cost: number }> {
  const byId = new Map(items.map((i) => [i.id, i]))
  const rows = new Map<string, { product_id: string | null; name: string; qty: number; revenue: number; cost: number }>()
  for (const it of items) {
    const dir = sign(it.sale_id)
    const owner = lineOwner(it, byId)
    const key = owner.product_id ?? owner.product_name
    const row = rows.get(key) ?? { product_id: owner.product_id ?? null, name: owner.product_name, qty: 0, revenue: 0, cost: 0 }
    if (owner === it) {
      row.qty += dir * it.quantity
      row.revenue += dir * revenueOf(it)
    }
    row.cost += dir * unitCostOf(it) * it.quantity
    rows.set(key, row)
  }
  return rows
}

/** The drink a part belongs to (itself for a drink, or for a part whose drink is not at hand). */
export function lineOwner<T extends { id: string; ingredient_of?: string | null }>(item: T, byId: Map<string, T>): T {
  return (item.ingredient_of != null && byId.get(item.ingredient_of)) || item
}
